import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { api } from '../App';
import { BookOpen, LogOut, Users, BarChart3, TrendingUp, Lightbulb, Eye, FileText, User } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16', '#f97316'];

export default function TeacherDashboardNew({ user, logout, setUser }) {
  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [classAnalytics, setClassAnalytics] = useState(null);
  const [classStudents, setClassStudents] = useState([]);
  const [recommendations, setRecommendations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);
  const [activeTab, setActiveTab] = useState('analytics');

  useEffect(() => {
    fetchClasses();
  }, []);

  const fetchClasses = async () => {
    try {
      const response = await api.get('/institutions/classes');
      setClasses(response.data);
      
      // Auto-select first class if available
      if (response.data.length > 0) {
        handleClassSelect(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch classes:', error);
      toast.error('Sınıflar yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleClassSelect = async (classId) => {
    setSelectedClass(classId);
    setClassAnalytics(null);
    setRecommendations(null);
    setClassStudents([]);
    
    try {
      const [analyticsRes, studentsRes] = await Promise.all([
        api.get(`/analytics/classes/${classId}`),
        api.get(`/institutions/classes/${classId}/students`)
      ]);
      setClassAnalytics(analyticsRes.data);
      setClassStudents(studentsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Veriler yüklenirken hata oluştu');
    }
  };

  const fetchRecommendations = async () => {
    if (!selectedClass) return;
    
    setLoadingRecommendations(true);
    try {
      const response = await api.get(`/analytics/classes/${selectedClass}/recommendations`);
      setRecommendations(response.data.recommendations);
      toast.success('AI önerileri başarıyla yüklendi');
    } catch (error) {
      console.error('Failed to fetch recommendations:', error);
      toast.error('Öneriler yüklenirken hata oluştu');
    } finally {
      setLoadingRecommendations(false);
    }
  };

  const startSelfAssessment = async () => {
    try {
      const response = await api.post('/assessments', {
        assessment_type: 'adult_self'
      });
      navigate(`/assessment/${response.data.id}`);
    } catch (error) {
      console.error('Failed to start self assessment:', error);
      toast.error('Envanter başlatılamadı');
    }
  };

  const selectedClassData = classes.find(c => c.id === selectedClass);

  // Prepare chart data
  const pieChartData = classAnalytics?.analytics?.distribution?.map(item => ({
    name: item.name,
    value: item.count,
    percentage: item.percentage
  })) || [];

  const barChartData = classAnalytics?.analytics?.distribution?.map(item => ({
    name: item.name.split(' / ')[0], // Shorten name for better display
    count: item.count,
    percentage: item.percentage
  })) || [];

  const handleStopImpersonation = async () => {
    try {
      const response = await api.post('/impersonate/stop');
      
      const { access_token, user: adminUser } = response.data;
      localStorage.setItem('access_token', access_token);
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser(adminUser);
      
      toast.success('Süper admin hesabınıza döndünüz');
      navigate('/');
    } catch (error) {
      console.error('Stop impersonation failed:', error);
      toast.error('Oturum sonlandırılamadı');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-emerald-50">
      {/* Header */}
      <header className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-emerald-600 rounded-xl flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-800">Mizaç360</h1>
              <p className="text-sm text-slate-600">Öğretmen Paneli</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {user.impersonation?.active && (
              <Badge className="bg-orange-100 text-orange-700 animate-pulse">
                Taklit Modu: {user.impersonation.admin_name}
              </Badge>
            )}
            <span className="text-sm text-slate-600">Merhaba, {user.name}</span>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate('/coach')}
              className="bg-gradient-to-r from-blue-50 to-emerald-50 border-blue-200 hover:bg-blue-100"
            >
              💬 Koçumla Konuş
            </Button>
            {user.impersonation?.active ? (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleStopImpersonation}
                className="bg-orange-50 border-orange-200"
              >
                Taklidi Durdur
              </Button>
            ) : null}
            <Button variant="outline" size="sm" onClick={logout}>
              <LogOut className="w-4 h-4 mr-2" />
              Çıkış
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-slate-800 mb-2">Öğretmen Panelinize Hoş Geldiniz</h2>
          <p className="text-slate-600">Hem kendi hem de sınıflarınızın mizaç profillerini keşfedin</p>
        </div>

        {/* Self Assessment Section */}
        <Card className="shadow-lg border-none mb-8 bg-gradient-to-br from-purple-50 to-pink-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-purple-600" />
              Kendi Mizaç Envanterim
            </CardTitle>
            <CardDescription>Kendi mizaç profilinizi keşfedin</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm text-slate-600 mb-3">
                  Kendiniz için mizaç envanteri doldurarak kendi özelliklerinizi keşfedebilir, 
                  öğrencilerinizle ilişkinizi daha iyi anlayabilirsiniz.
                </p>
                <Button
                  className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                  onClick={startSelfAssessment}
                >
                  <BookOpen className="w-4 h-4 mr-2" />
                  Kendi Envanterimi Doldur
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : classes.length === 0 ? (
          <Card className="shadow-lg border-none">
            <CardContent className="text-center py-12">
              <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 mb-4">Henüz atanmış sınıfınız bulunmuyor</p>
              <p className="text-sm text-slate-400">Kurum yöneticiniz tarafından sınıflara atanmanızı bekleyin</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Class Selector */}
            <Card className="shadow-lg border-none">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-600" />
                  Sınıf Seçimi
                </CardTitle>
                <CardDescription>Görüntülemek istediğiniz sınıfı seçin</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-3">
                  {classes.map((cls) => (
                    <Button
                      key={cls.id}
                      variant={selectedClass === cls.id ? "default" : "outline"}
                      className={selectedClass === cls.id ? "bg-gradient-to-r from-blue-600 to-emerald-600" : ""}
                      onClick={() => handleClassSelect(cls.id)}
                    >
                      {cls.name} ({cls.grade_level}. Sınıf)
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Tabs for Analytics and Students */}
            {selectedClass && (
              <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="analytics">Mizaç Analizi</TabsTrigger>
                  <TabsTrigger value="students">Öğrenci Listesi</TabsTrigger>
                </TabsList>

                {/* Analytics Tab */}
                <TabsContent value="analytics" className="space-y-6">
                  {classAnalytics && (
                    <>
                      {/* Stats Overview */}
                      <div className="grid md:grid-cols-3 gap-6">
                        <Card className="shadow-lg border-none">
                          <CardHeader className="pb-3">
                            <CardDescription>Toplam Öğrenci</CardDescription>
                            <CardTitle className="text-3xl">{classAnalytics.analytics.total_students}</CardTitle>
                          </CardHeader>
                        </Card>
                        <Card className="shadow-lg border-none">
                          <CardHeader className="pb-3">
                            <CardDescription>Envanter Tamamlanan</CardDescription>
                            <CardTitle className="text-3xl text-emerald-600">
                              {classAnalytics.analytics.students_with_reports}
                            </CardTitle>
                          </CardHeader>
                        </Card>
                        <Card className="shadow-lg border-none">
                          <CardHeader className="pb-3">
                            <CardDescription>Tamamlanma Oranı</CardDescription>
                            <CardTitle className="text-3xl text-blue-600">
                              {classAnalytics.analytics.total_students > 0
                                ? Math.round((classAnalytics.analytics.students_with_reports / classAnalytics.analytics.total_students) * 100)
                                : 0}%
                            </CardTitle>
                          </CardHeader>
                        </Card>
                      </div>

                      {/* Charts */}
                      {classAnalytics.analytics.students_with_reports > 0 ? (
                        <div className="grid md:grid-cols-2 gap-6">
                          {/* Pie Chart */}
                          <Card className="shadow-lg border-none">
                            <CardHeader>
                              <CardTitle className="flex items-center gap-2">
                                <BarChart3 className="w-5 h-5 text-blue-600" />
                                Mizaç Dağılımı (Pasta Grafik)
                              </CardTitle>
                              <CardDescription>Sınıfınızdaki mizaç tiplerinin oransal dağılımı</CardDescription>
                            </CardHeader>
                            <CardContent>
                              <ResponsiveContainer width="100%" height={300}>
                                <PieChart>
                                  <Pie
                                    data={pieChartData}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={false}
                                    label={({ name, percentage }) => `${name.split(' / ')[0]}: ${percentage}%`}
                                    outerRadius={80}
                                    fill="#8884d8"
                                    dataKey="value"
                                  >
                                    {pieChartData.map((entry, index) => (
                                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                  </Pie>
                                  <Tooltip />
                                </PieChart>
                              </ResponsiveContainer>
                            </CardContent>
                          </Card>

                          {/* Bar Chart */}
                          <Card className="shadow-lg border-none">
                            <CardHeader>
                              <CardTitle className="flex items-center gap-2">
                                <TrendingUp className="w-5 h-5 text-emerald-600" />
                                Mizaç Dağılımı (Bar Grafik)
                              </CardTitle>
                              <CardDescription>Öğrenci sayılarının karşılaştırması</CardDescription>
                            </CardHeader>
                            <CardContent>
                              <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={barChartData}>
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                                  <YAxis />
                                  <Tooltip />
                                  <Bar dataKey="count" fill="#3b82f6" />
                                </BarChart>
                              </ResponsiveContainer>
                            </CardContent>
                          </Card>
                        </div>
                      ) : (
                        <Card className="shadow-lg border-none">
                          <CardContent className="text-center py-12">
                            <BarChart3 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                            <p className="text-slate-500">Henüz mizaç verisi bulunmuyor</p>
                            <p className="text-sm text-slate-400 mt-2">
                              Öğrencileriniz envanteri doldurduğunda grafikler burada görünecek
                            </p>
                          </CardContent>
                        </Card>
                      )}

                      {/* AI Recommendations */}
                      <Card className="shadow-lg border-none">
                        <CardHeader>
                          <div className="flex justify-between items-start">
                            <div>
                              <CardTitle className="flex items-center gap-2">
                                <Lightbulb className="w-5 h-5 text-yellow-600" />
                                AI Destekli Sınıf Önerileri
                              </CardTitle>
                              <CardDescription>
                                Sınıfınızın mizaç dağılımına özel stratejiler ve öneriler
                              </CardDescription>
                            </div>
                            <Button
                              onClick={fetchRecommendations}
                              disabled={loadingRecommendations || classAnalytics.analytics.students_with_reports === 0}
                              className="bg-gradient-to-r from-yellow-500 to-orange-600 hover:from-yellow-600 hover:to-orange-700"
                            >
                              {loadingRecommendations ? 'Yükleniyor...' : 'Öneriler Oluştur'}
                            </Button>
                          </div>
                        </CardHeader>
                        <CardContent>
                          {recommendations ? (
                            <div className="prose prose-slate max-w-none">
                              <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-6 rounded-lg border border-yellow-200">
                                <p className="whitespace-pre-wrap text-slate-700 leading-relaxed">{recommendations}</p>
                              </div>
                            </div>
                          ) : (
                            <div className="text-center py-8 text-slate-500">
                              <Lightbulb className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                              <p>Sınıfınıza özel AI destekli öneriler almak için yukarıdaki butona tıklayın</p>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </>
                  )}
                </TabsContent>

                {/* Students Tab */}
                <TabsContent value="students" className="space-y-6">
                  <Card className="shadow-lg border-none">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <User className="w-5 h-5 text-purple-600" />
                        Öğrenci Listesi
                      </CardTitle>
                      <CardDescription>
                        Sınıfınızdaki öğrenciler ve envanter durumları
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {classStudents.length > 0 ? (
                        <div className="space-y-3">
                          {classStudents.map((student) => (
                            <div
                              key={student.id}
                              className="flex justify-between items-center p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                            >
                              <div className="flex items-center gap-4">
                                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white font-semibold">
                                  {student.name?.charAt(0)?.toUpperCase()}
                                </div>
                                <div>
                                  <p className="font-semibold text-slate-800">{student.name}</p>
                                  <p className="text-sm text-slate-500">{student.email}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                {student.has_report ? (
                                  <>
                                    <Badge className="bg-emerald-100 text-emerald-700">
                                      Tip {student.temperament_type || '?'}
                                    </Badge>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => navigate(`/report/${student.report_id}`)}
                                      className="bg-blue-50 border-blue-200"
                                    >
                                      <Eye className="w-4 h-4 mr-2" />
                                      Raporu Gör
                                    </Button>
                                  </>
                                ) : (
                                  <Badge variant="outline" className="text-slate-500">
                                    <FileText className="w-3 h-3 mr-1" />
                                    Envanter bekleniyor
                                  </Badge>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-12">
                          <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                          <p className="text-slate-500">Bu sınıfta henüz öğrenci bulunmuyor</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
